import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { listApiKeys } from '../lib/api-client';
import { ensureOneApiKey, type HeldApiKey } from '../lib/ensure-api-key';
import { useAuth } from '../features/onboarding/AuthContext';

/**
 * TTP-244's dashboard session key — see this issue's brief, "Decisão
 * arquitetural CRÍTICA — chave de sessão do dashboard".
 *
 * `/v1/*` (labels, providers, routing-policy — everything the post-
 * onboarding dashboard calls) never accepts the Firebase ID token, only a
 * `trv_*` API key. The two keys onboarding mints (`ApiKeysContext.tsx`) are
 * revealed to the user exactly ONCE, at Tela 5 (`ApiKeyStep.tsx`) — after
 * that screen, their raw value exists nowhere else, ever again (the backend
 * only returns `key` on creation, `ApiKeysService#create`). If the dashboard
 * reused those same keys by calling the onboarding pair's idempotent
 * find-or-mint helper again on every reload, it would find the key already
 * exists, NOT have the raw value in memory (lost on reload), and — by that
 * helper's own correct-for-onboarding design — revoke and reissue it. That
 * would be the lojista's real `trv_live_` key, possibly already wired into
 * their own external integration outside this dashboard; silently rotating
 * it on every dashboard F5 would break that integration.
 *
 * The fix: a THIRD key, reserved by this fixed name, `mode: 'live'` always
 * (no live/test toggle in this issue — the dashboard shows real org data),
 * invisible to the user, used ONLY by this app's own `/v1` calls. Reusing
 * `ensureOneApiKey` (same mechanics as onboarding: find-or-mint by exact
 * `(mode, name)`, revoke-and-replace an orphan) is safe here specifically
 * BECAUSE this name never matches the onboarding pair's names — see
 * `ensure-api-key.test.ts`'s "ignores a key with a different name entirely"
 * case, which pins that as a property of `ensureOneApiKey` itself, not just
 * an assumption of this file. Revoking/reissuing THIS key on every reload is
 * fine: nothing outside the dashboard ever sees or uses it.
 */
export const DASHBOARD_SESSION_KEY_NAME = '__trivio_dashboard_session__';

interface DashboardSessionKeyContextValue {
  /** `null` until the first successful ensure; every `/v1` call in the dashboard should wait for this before firing. */
  key: HeldApiKey | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

const DashboardSessionKeyContext = createContext<DashboardSessionKeyContextValue | null>(null);

/**
 * Mounted once, at the top of `AppShell.tsx`, wrapping every post-onboarding
 * route (`/labels`, `/labels/:id`, `/providers`, `/api-keys`, `/routing`,
 * `/usage`).
 * Re-ensures the session key whenever `orgId` changes (org switcher) —
 * `createApiKey`/`listApiKeys` resolve the org from the ID token's `orgId`
 * claim server-side (`requireOrgId`), so a session key minted after
 * switching org is scoped to the NEW org, same isolation guarantee as every
 * other org-scoped fetch in this app.
 */
export function DashboardSessionKeyProvider({ children }: { children: ReactNode }) {
  const { user, orgId } = useAuth();
  const [key, setKey] = useState<HeldApiKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const heldRef = useRef<HeldApiKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    // A new org (or a fresh sign-in) never carries over the previous org's
    // session key — isolation must be visible immediately, not just on the
    // next successful ensure.
    heldRef.current = null;
    setKey(null);

    if (!user || !orgId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      const idToken = await user.getIdToken();
      const existingMetadata = await listApiKeys(idToken);
      return ensureOneApiKey(idToken, 'live', DASHBOARD_SESSION_KEY_NAME, heldRef.current, existingMetadata);
    })()
      .then((ensured) => {
        if (cancelled) return;
        heldRef.current = ensured;
        setKey(ensured);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Falha ao preparar a sessão do dashboard.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, orgId, attempt]);

  const value: DashboardSessionKeyContextValue = {
    key,
    loading,
    error,
    retry: () => setAttempt((a) => a + 1),
  };

  return <DashboardSessionKeyContext.Provider value={value}>{children}</DashboardSessionKeyContext.Provider>;
}

export function useDashboardSessionKey(): DashboardSessionKeyContextValue {
  const ctx = useContext(DashboardSessionKeyContext);
  if (!ctx) {
    throw new Error('useDashboardSessionKey must be used within a DashboardSessionKeyProvider');
  }
  return ctx;
}
